// src/components/AISettings.tsx
import React, { useState, useEffect, useRef } from "react";
import PageHeader from "./PageHeader.tsx";
import { TAB_DESCRIPTIONS } from "../constants/tabDescriptions.ts";
import {
  SavedAIConfig,
  getAIConfigs,
  saveAIConfig,
  deleteAIConfig,
  getAIConfigCount,
  formatConfigDate,
  exportAIConfig,
  importAIConfig,
} from "../utils/aiConfigStorage.ts";
import { NotificationModal, NotificationState, initialNotificationState } from "./modals/NotificationModal.tsx";
import "./AISettings.css";

// v1.30.0: 6-level difficulty system
export type DifficultyLevel = "beginner" | "casual" | "intermediate" | "advanced" | "expert" | "master";
export type EngineType = "rust" | "typescript";

export interface AISettingsState {
  difficulty: DifficultyLevel;
  // Primary settings
  maxThinkingTime: number;      // Time limit in seconds
  threadCount: number;          // Number of threads for Rust engine (1-12)
  depthCap: number;             // Maximum search depth (4-12)
  // Secondary settings - Evaluation weights
  materialBalance: number;
  piecePositioning: number;
  kingSafety: number;
  mobility: number;
  pawnStructure: boolean;
  boardControl: boolean;
  // Tertiary settings - Engine internals
  quiescenceDepth: number;      // Max quiescence depth (16-40)
  aspirationWindow: number;     // Aspiration window size (30-100)
  lmrThreshold: number;         // LMR move threshold (2-6)
  nullMoveR: number;            // Null move reduction R (2-3)
  ttSizeMb: number;             // Transposition table size in MB (64-1024)
  // Legacy settings (TypeScript engine)
  priorityOrder: string[];
  useIterativeDeepening: boolean;
  useAlphaBetaPruning: boolean;
  engineType: EngineType;
}

interface AISettingsProps {
  onSettingsChange: (settings: AISettingsState) => void;
  initialSettings?: AISettingsState;
}

// v1.30.0: Default to Intermediate level settings
const DEFAULT_SETTINGS: AISettingsState = {
  difficulty: "intermediate",
  // Primary settings (Intermediate level - updated for better tactical awareness)
  maxThinkingTime: 45,
  threadCount: 6,
  depthCap: 8,
  // Secondary settings - Evaluation weights
  materialBalance: 1.0,
  piecePositioning: 0.8,
  kingSafety: 0.6,
  mobility: 0.0,
  pawnStructure: true,
  boardControl: false,
  // Tertiary settings - Engine internals
  quiescenceDepth: 26,
  aspirationWindow: 60,
  lmrThreshold: 4,
  nullMoveR: 2,
  ttSizeMb: 256,
  // Legacy settings (TypeScript engine)
  priorityOrder: [
    "checkmate",
    "capture_high",
    "check",
    "exchange_favorable",
    "fork",
    "threat",
    "development",
    "castling",
  ],
  useIterativeDeepening: true,
  useAlphaBetaPruning: true,
  engineType: "rust",
};

const PRIORITY_LABELS: Record<string, string> = {
  checkmate: "Checkmate Moves",
  capture_high: "High-Value Captures",
  check: "Check Moves",
  exchange_favorable: "Favorable Exchanges",
  fork: "Fork Opportunities",
  threat: "Create Threats",
  development: "Piece Development",
  castling: "Castling",
};

const AISettings: React.FC<AISettingsProps> = ({
  onSettingsChange,
  initialSettings,
}) => {
  const [settings, setSettings] = useState<AISettingsState>(
    initialSettings || DEFAULT_SETTINGS
  );
  const [hasChanges, setHasChanges] = useState(false);

  // AI Config Profiles state
  const [savedConfigs, setSavedConfigs] = useState<SavedAIConfig[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [configName, setConfigName] = useState("");
  const [configDescription, setConfigDescription] = useState("");
  const [configError, setConfigError] = useState("");
  const [configSuccess, setConfigSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notification modal state (replaces native alert/confirm dialogs)
  const [notification, setNotification] = useState<NotificationState>(initialNotificationState);
  const [pendingDeleteConfig, setPendingDeleteConfig] = useState<SavedAIConfig | null>(null);

  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  // Load saved configs on mount
  useEffect(() => {
    const loadConfigs = async () => {
      const configs = await getAIConfigs();
      setSavedConfigs(configs);
    };
    loadConfigs();
  }, []);

  // v1.30.0: 6-level difficulty system with full configuration
  const handleDifficultyChange = (difficulty: DifficultyLevel) => {
    let newSettings = { ...settings, difficulty };

    // Apply all settings based on difficulty level (from finalized table)
    switch (difficulty) {
      case "beginner":
        newSettings = {
          ...newSettings,
          // Primary
          maxThinkingTime: 10,
          threadCount: 2,
          depthCap: 4,
          // Secondary - Evaluation
          materialBalance: 1.0,
          piecePositioning: 0.3,
          kingSafety: 0.2,
          mobility: 0.0,
          pawnStructure: false,
          boardControl: false,
          // Tertiary - Engine internals
          quiescenceDepth: 16,
          aspirationWindow: 100,
          lmrThreshold: 2,
          nullMoveR: 3,
          ttSizeMb: 64,
        };
        break;
      case "casual":
        newSettings = {
          ...newSettings,
          // Primary
          maxThinkingTime: 20,
          threadCount: 4,
          depthCap: 6,
          // Secondary - Evaluation
          materialBalance: 1.0,
          piecePositioning: 0.5,
          kingSafety: 0.4,
          mobility: 0.0,
          pawnStructure: false,
          boardControl: false,
          // Tertiary - Engine internals
          quiescenceDepth: 20,
          aspirationWindow: 80,
          lmrThreshold: 3,
          nullMoveR: 3,
          ttSizeMb: 128,
        };
        break;
      case "intermediate":
        newSettings = {
          ...newSettings,
          // Primary (updated for better tactical awareness)
          maxThinkingTime: 45,
          threadCount: 6,
          depthCap: 8,
          // Secondary - Evaluation
          materialBalance: 1.0,
          piecePositioning: 0.8,
          kingSafety: 0.6,
          mobility: 0.0,
          pawnStructure: true,
          boardControl: false,
          // Tertiary - Engine internals
          quiescenceDepth: 26,
          aspirationWindow: 60,
          lmrThreshold: 4,
          nullMoveR: 2,
          ttSizeMb: 256,
        };
        break;
      case "advanced":
        newSettings = {
          ...newSettings,
          // Primary (updated)
          maxThinkingTime: 75,
          threadCount: 8,
          depthCap: 10,
          // Secondary - Evaluation
          materialBalance: 1.0,
          piecePositioning: 1.0,
          kingSafety: 0.8,
          mobility: 0.1,
          pawnStructure: true,
          boardControl: false,
          // Tertiary - Engine internals
          quiescenceDepth: 30,
          aspirationWindow: 50,
          lmrThreshold: 4,
          nullMoveR: 2,
          ttSizeMb: 384,
        };
        break;
      case "expert":
        newSettings = {
          ...newSettings,
          // Primary (updated)
          maxThinkingTime: 120,
          threadCount: 10,
          depthCap: 12,
          // Secondary - Evaluation
          materialBalance: 1.0,
          piecePositioning: 1.0,
          kingSafety: 1.0,
          mobility: 0.2,
          pawnStructure: true,
          boardControl: true,
          // Tertiary - Engine internals
          quiescenceDepth: 34,
          aspirationWindow: 40,
          lmrThreshold: 5,
          nullMoveR: 2,
          ttSizeMb: 512,
        };
        break;
      case "master":
        newSettings = {
          ...newSettings,
          // Primary (updated)
          maxThinkingTime: 180,
          threadCount: 12,
          depthCap: 14,
          // Secondary - Evaluation
          materialBalance: 1.0,
          piecePositioning: 1.0,
          kingSafety: 1.0,
          mobility: 0.3,
          pawnStructure: true,
          boardControl: true,
          // Tertiary - Engine internals
          quiescenceDepth: 40,
          aspirationWindow: 30,
          lmrThreshold: 6,
          nullMoveR: 2,
          ttSizeMb: 768,
        };
        break;
    }

    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleWeightChange = (weight: keyof AISettingsState, value: number) => {
    const newSettings = { ...settings, [weight]: value };

    // Normalize weights to ensure they sum to 1.0
    const totalWeight =
      newSettings.materialBalance +
      newSettings.piecePositioning +
      newSettings.kingSafety +
      newSettings.mobility;

    if (totalWeight > 0) {
      newSettings.materialBalance = newSettings.materialBalance / totalWeight;
      newSettings.piecePositioning = newSettings.piecePositioning / totalWeight;
      newSettings.kingSafety = newSettings.kingSafety / totalWeight;
      newSettings.mobility = newSettings.mobility / totalWeight;
    }

    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleBooleanChange = (key: keyof AISettingsState, value: boolean) => {
    setSettings({ ...settings, [key]: value });
    setHasChanges(true);
  };

  const handleThinkingTimeChange = (value: number) => {
    setSettings({ ...settings, maxThinkingTime: value });
    setHasChanges(true);
  };

  const handleThreadCountChange = (value: number) => {
    setSettings({ ...settings, threadCount: value });
    setHasChanges(true);
  };

  const handleEngineTypeChange = (value: EngineType) => {
    setSettings({ ...settings, engineType: value });
    setHasChanges(true);
  };

  // Handlers for advanced Rust engine settings
  const handleDepthCapChange = (value: number) => {
    setSettings({ ...settings, depthCap: value });
    setHasChanges(true);
  };

  const handleQuiescenceDepthChange = (value: number) => {
    setSettings({ ...settings, quiescenceDepth: value });
    setHasChanges(true);
  };

  const handleAspirationWindowChange = (value: number) => {
    setSettings({ ...settings, aspirationWindow: value });
    setHasChanges(true);
  };

  const handleLmrThresholdChange = (value: number) => {
    setSettings({ ...settings, lmrThreshold: value });
    setHasChanges(true);
  };

  const handleNullMoveRChange = (value: number) => {
    setSettings({ ...settings, nullMoveR: value });
    setHasChanges(true);
  };

  const handleTtSizeMbChange = (value: number) => {
    setSettings({ ...settings, ttSizeMb: value });
    setHasChanges(true);
  };

  // State for collapsible advanced settings
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // v1.30.0: Max thinking time per difficulty level (updated)
  const getMaxThinkingTimeForDifficulty = (
    difficulty: DifficultyLevel
  ): number => {
    switch (difficulty) {
      case "beginner":
        return 10;
      case "casual":
        return 20;
      case "intermediate":
        return 45;
      case "advanced":
        return 75;
      case "expert":
        return 120;
      case "master":
        return 180;
      default:
        return 180;
    }
  };

  const isAtMaxThinkingTime = (): boolean => {
    const maxTime = getMaxThinkingTimeForDifficulty(settings.difficulty);
    return settings.maxThinkingTime >= maxTime;
  };

  const handlePriorityChange = (index: number, direction: "up" | "down") => {
    const newPriorityOrder = [...settings.priorityOrder];
    const swapIndex = direction === "up" ? index - 1 : index + 1;

    if (swapIndex >= 0 && swapIndex < newPriorityOrder.length) {
      [newPriorityOrder[index], newPriorityOrder[swapIndex]] = [
        newPriorityOrder[swapIndex],
        newPriorityOrder[index],
      ];

      setSettings({ ...settings, priorityOrder: newPriorityOrder });
      setHasChanges(true);
    }
  };

  const handleApplySettings = () => {
    onSettingsChange(settings);
    setHasChanges(false);
  };

  const handleResetToDefaults = () => {
    setSettings(DEFAULT_SETTINGS);
    setHasChanges(true);
  };

  // === AI Config Profile Handlers ===

  const handleSaveConfig = async () => {
    setConfigError("");
    setConfigSuccess("");
    setIsSaving(true);

    try {
      const newConfig = await saveAIConfig(configName, configDescription, settings);
      const configs = await getAIConfigs();
      setSavedConfigs(configs);
      setConfigName("");
      setConfigDescription("");
      setConfigSuccess(`Configuration "${newConfig.name}" saved successfully!`);
    } catch (error: any) {
      setConfigError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const closeSaveDialog = () => {
    setShowSaveDialog(false);
    setConfigName("");
    setConfigDescription("");
    setConfigError("");
    setConfigSuccess("");
  };

  const handleLoadConfig = (config: SavedAIConfig) => {
    setSettings(config.settings);
    setHasChanges(true);
    setNotification({
      isOpen: true,
      type: "success",
      title: "Configuration Loaded",
      message: `Configuration "${config.name}" loaded!\n\nClick "Apply Changes" to use these settings.`,
    });
  };

  const handleDeleteConfig = (config: SavedAIConfig) => {
    setPendingDeleteConfig(config);
    setNotification({
      isOpen: true,
      type: "confirm",
      title: "Delete Configuration",
      message: `Delete configuration "${config.name}"?\n\nThis action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });
  };

  const confirmDeleteConfig = async () => {
    if (!pendingDeleteConfig) return;

    try {
      await deleteAIConfig(pendingDeleteConfig.id);
      const configs = await getAIConfigs();
      setSavedConfigs(configs);
      setNotification({
        isOpen: true,
        type: "success",
        title: "Configuration Deleted",
        message: `Configuration "${pendingDeleteConfig.name}" has been deleted.`,
      });
    } catch (error: any) {
      setNotification({
        isOpen: true,
        type: "error",
        title: "Delete Failed",
        message: error.message || "Failed to delete configuration.",
      });
    } finally {
      setPendingDeleteConfig(null);
    }
  };

  const handleExportConfig = (config: SavedAIConfig) => {
    exportAIConfig(config);
  };

  const handleImportConfig = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const imported = await importAIConfig(file);
      const configs = await getAIConfigs();
      setSavedConfigs(configs);
      setNotification({
        isOpen: true,
        type: "success",
        title: "Import Successful",
        message: `Configuration "${imported.name}" has been imported successfully!`,
      });
    } catch (error: any) {
      setNotification({
        isOpen: true,
        type: "error",
        title: "Import Failed",
        message: error.message || "Failed to import configuration.",
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Config count state (loaded async)
  const [configCount, setConfigCount] = useState({ count: 0, limit: 12, remaining: 12 });

  useEffect(() => {
    const loadCount = async () => {
      const count = await getAIConfigCount();
      setConfigCount(count);
    };
    loadCount();
  }, [savedConfigs]);

  return (
    <div className="ai-settings-container">
      <PageHeader
        icon={TAB_DESCRIPTIONS.aiSettings.icon}
        title={TAB_DESCRIPTIONS.aiSettings.label}
        description={TAB_DESCRIPTIONS.aiSettings.description}
        actions={
          <>
            <button
              className="action-btn action-btn-danger"
              onClick={handleResetToDefaults}
            >
              Reset to Defaults
            </button>
            <button
              className={`action-btn ${hasChanges ? "action-btn-primary" : ""}`}
              onClick={handleApplySettings}
              disabled={!hasChanges}
              style={
                !hasChanges
                  ? {
                      backgroundColor: "#ccc",
                      color: "#666",
                      cursor: "not-allowed",
                    }
                  : {}
              }
            >
              {hasChanges ? "Apply Changes" : "No Changes"}
            </button>
          </>
        }
      />

      <div className="settings-content">
        {/* AI Config Profiles - Full Width */}
        <div className="settings-section config-profiles-section">
          <h2>AI Config Profiles</h2>
          <p className="section-description">
            Save and load AI configuration profiles ({configCount.count}/{configCount.limit} saved)
          </p>

          {/* Save/Import Buttons */}
          <div className="config-actions">
            <button
              className="config-btn config-btn-save"
              onClick={() => setShowSaveDialog(true)}
              disabled={configCount.remaining <= 0}
              title={
                configCount.remaining <= 0
                  ? "Maximum configurations reached"
                  : "Save current configuration"
              }
            >
              <span className="btn-icon">💾</span> Save Current
            </button>
            <button
              className="config-btn config-btn-import"
              onClick={() => fileInputRef.current?.click()}
            >
              <span className="btn-icon">📥</span> Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportConfig}
              style={{ display: "none" }}
            />
          </div>

          {/* Saved Configs List */}
          {savedConfigs.length > 0 && (
            <div className="saved-configs-list">
              {savedConfigs.map((config) => (
                <div key={config.id} className="saved-config-item">
                  <div className="config-info">
                    <span className="config-name">{config.name}</span>
                    {config.description && (
                      <span className="config-desc">{config.description}</span>
                    )}
                    <span className="config-date">
                      {formatConfigDate(config.updatedAt)}
                    </span>
                  </div>
                  <div className="config-item-actions">
                    <button
                      className="config-item-btn load"
                      onClick={() => handleLoadConfig(config)}
                      title="Load this configuration"
                    >
                      Load
                    </button>
                    <button
                      className="config-item-btn export"
                      onClick={() => handleExportConfig(config)}
                      title="Export to file"
                    >
                      Export
                    </button>
                    <button
                      className="config-item-btn delete"
                      onClick={() => handleDeleteConfig(config)}
                      title="Delete this configuration"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {savedConfigs.length === 0 && (
            <div className="no-configs-message">
              No saved configurations yet. Save your current settings to create a profile.
            </div>
          )}
        </div>

        {/* Difficulty Level - v1.30.0: 6 levels */}
        <div className="settings-section compact-section">
          <h2>Difficulty Level</h2>
          <div className="difficulty-buttons">
            {(
              ["beginner", "casual", "intermediate", "advanced", "expert", "master"] as DifficultyLevel[]
            ).map((level) => (
              <button
                key={level}
                className={`difficulty-btn ${
                  settings.difficulty === level ? "active" : ""
                }`}
                onClick={() => handleDifficultyChange(level)}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Thinking Time */}
        <div className="settings-section compact-section">
          <h2>Thinking Time</h2>
          <div className="time-control">
            <label>
              Maximum Time per Move
              <span className="time-value">
                {settings.maxThinkingTime} seconds
              </span>
            </label>
            <input
              type="range"
              min="5"
              max={getMaxThinkingTimeForDifficulty(settings.difficulty)}
              step="5"
              value={settings.maxThinkingTime}
              onChange={(e) =>
                handleThinkingTimeChange(parseInt(e.target.value))
              }
              className={isAtMaxThinkingTime() ? "at-max-value" : ""}
            />
            <div className="time-markers">
              <span>5s</span>
              <span>
                {Math.floor(
                  getMaxThinkingTimeForDifficulty(settings.difficulty) / 3
                )}
                s
              </span>
              <span>
                {Math.floor(
                  (getMaxThinkingTimeForDifficulty(settings.difficulty) * 2) / 3
                )}
                s
              </span>
              <span>
                {getMaxThinkingTimeForDifficulty(settings.difficulty)}s
              </span>
            </div>
          </div>
        </div>

        {/* AI Engine Selection */}
        <div className="settings-section compact-section">
          <h2>AI Engine</h2>
          <p className="section-description">
            Choose the chess engine for AI calculations
          </p>
          <div className="engine-selector">
            <button
              className={`engine-btn ${settings.engineType === "rust" ? "active" : ""}`}
              onClick={() => handleEngineTypeChange("rust")}
            >
              <span className="engine-icon">🦀</span>
              <span className="engine-name">Rust</span>
              <span className="engine-desc">Multi-core server</span>
            </button>
            <button
              className={`engine-btn ${settings.engineType === "typescript" ? "active" : ""}`}
              onClick={() => handleEngineTypeChange("typescript")}
            >
              <span className="engine-icon">📜</span>
              <span className="engine-name">TypeScript</span>
              <span className="engine-desc">Browser-based</span>
            </button>
          </div>
        </div>

        {/* Thread Count (Native Engine) - Only show for Rust engine */}
        {settings.engineType === "rust" && (
        <div className="settings-section compact-section">
          <h2>Engine Threads</h2>
          <p className="section-description">
            Number of threads for the Rust engine (max 12 for Master level)
          </p>
          <div className="time-control">
            <label>
              Parallel Search Threads
              <span className="time-value">{settings.threadCount} thread{settings.threadCount !== 1 ? 's' : ''}</span>
            </label>
            <input
              type="range"
              min="1"
              max="12"
              step="1"
              value={settings.threadCount}
              onChange={(e) =>
                handleThreadCountChange(parseInt(e.target.value))
              }
              className={settings.threadCount >= 10 ? "high-threads" : ""}
            />
            <div className="time-markers">
              <span>1</span>
              <span>4</span>
              <span>8</span>
              <span>12</span>
            </div>
          </div>
        </div>
        )}

        {/* Advanced Rust Engine Settings - Collapsible */}
        {settings.engineType === "rust" && (
        <div className="settings-section">
          <h2
            className="collapsible-header"
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            Advanced Engine Settings {showAdvancedSettings ? '▼' : '▶'}
          </h2>
          <p className="section-description">
            Fine-tune Rust engine internals (auto-set by difficulty level)
          </p>

          {showAdvancedSettings && (
            <>
              {/* Search Depth Cap */}
              <div className="time-control" style={{ marginBottom: '12px' }}>
                <label>
                  Search Depth Cap
                  <span className="time-value">{settings.depthCap} ply</span>
                </label>
                <input
                  type="range"
                  min="4"
                  max="14"
                  step="1"
                  value={settings.depthCap}
                  onChange={(e) => handleDepthCapChange(parseInt(e.target.value))}
                />
                <div className="time-markers">
                  <span>4</span>
                  <span>8</span>
                  <span>10</span>
                  <span>12</span>
                  <span>14</span>
                </div>
              </div>

              {/* Quiescence Depth */}
              <div className="time-control" style={{ marginBottom: '12px' }}>
                <label>
                  Quiescence Depth
                  <span className="time-value">{settings.quiescenceDepth} ply</span>
                </label>
                <input
                  type="range"
                  min="16"
                  max="40"
                  step="4"
                  value={settings.quiescenceDepth}
                  onChange={(e) => handleQuiescenceDepthChange(parseInt(e.target.value))}
                />
                <div className="time-markers">
                  <span>16</span>
                  <span>24</span>
                  <span>32</span>
                  <span>40</span>
                </div>
              </div>

              {/* Aspiration Window */}
              <div className="time-control" style={{ marginBottom: '12px' }}>
                <label>
                  Aspiration Window
                  <span className="time-value">{settings.aspirationWindow} cp</span>
                </label>
                <input
                  type="range"
                  min="30"
                  max="100"
                  step="10"
                  value={settings.aspirationWindow}
                  onChange={(e) => handleAspirationWindowChange(parseInt(e.target.value))}
                />
                <div className="time-markers">
                  <span>30</span>
                  <span>50</span>
                  <span>70</span>
                  <span>100</span>
                </div>
              </div>

              {/* LMR Move Threshold */}
              <div className="time-control" style={{ marginBottom: '12px' }}>
                <label>
                  LMR Move Threshold
                  <span className="time-value">{settings.lmrThreshold} moves</span>
                </label>
                <input
                  type="range"
                  min="2"
                  max="6"
                  step="1"
                  value={settings.lmrThreshold}
                  onChange={(e) => handleLmrThresholdChange(parseInt(e.target.value))}
                />
                <div className="time-markers">
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                  <span>6</span>
                </div>
              </div>

              {/* Null Move Reduction */}
              <div className="time-control" style={{ marginBottom: '12px' }}>
                <label>
                  Null Move Reduction (R)
                  <span className="time-value">R={settings.nullMoveR}</span>
                </label>
                <input
                  type="range"
                  min="2"
                  max="3"
                  step="1"
                  value={settings.nullMoveR}
                  onChange={(e) => handleNullMoveRChange(parseInt(e.target.value))}
                />
                <div className="time-markers">
                  <span>2 (aggressive)</span>
                  <span>3 (conservative)</span>
                </div>
              </div>

              {/* Transposition Table Size */}
              <div className="time-control">
                <label>
                  Transposition Table Memory
                  <span className="time-value">{settings.ttSizeMb} MB</span>
                </label>
                <input
                  type="range"
                  min="64"
                  max="1024"
                  step="64"
                  value={settings.ttSizeMb}
                  onChange={(e) => handleTtSizeMbChange(parseInt(e.target.value))}
                />
                <div className="time-markers">
                  <span>64</span>
                  <span>384</span>
                  <span>704</span>
                  <span>1024</span>
                </div>
              </div>
            </>
          )}
        </div>
        )}

        {/* Evaluation Weights */}
        <div className="settings-section">
          <h2>Evaluation Weights</h2>
          <p className="section-description">
            Adjust how the AI values different aspects of position (weights are
            auto-normalized)
          </p>

          <div className="weight-control">
            <label>
              Material Balance
              <span className="weight-value">
                {(settings.materialBalance * 100).toFixed(1)}%
              </span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.materialBalance * 100}
              onChange={(e) =>
                handleWeightChange(
                  "materialBalance",
                  parseInt(e.target.value) / 100
                )
              }
            />
          </div>

          <div className="weight-control">
            <label>
              Piece Positioning
              <span className="weight-value">
                {(settings.piecePositioning * 100).toFixed(1)}%
              </span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.piecePositioning * 100}
              onChange={(e) =>
                handleWeightChange(
                  "piecePositioning",
                  parseInt(e.target.value) / 100
                )
              }
            />
          </div>

          <div className="weight-control">
            <label>
              King Safety
              <span className="weight-value">
                {(settings.kingSafety * 100).toFixed(1)}%
              </span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.kingSafety * 100}
              onChange={(e) =>
                handleWeightChange("kingSafety", parseInt(e.target.value) / 100)
              }
            />
          </div>

          <div className="weight-control">
            <label>
              Mobility
              <span className="weight-value">
                {(settings.mobility * 100).toFixed(1)}%
              </span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.mobility * 100}
              onChange={(e) =>
                handleWeightChange("mobility", parseInt(e.target.value) / 100)
              }
            />
          </div>
        </div>

        {/* Advanced Features */}
        <div className="settings-section">
          <h2>Advanced Features</h2>

          <div className="checkbox-control">
            <label>
              <input
                type="checkbox"
                checked={settings.useAlphaBetaPruning}
                onChange={(e) =>
                  handleBooleanChange("useAlphaBetaPruning", e.target.checked)
                }
              />
              <span>Alpha-Beta Pruning</span>
            </label>
            <p className="feature-description">
              Optimize search by eliminating poor branches
            </p>
          </div>

          <div className="checkbox-control">
            <label>
              <input
                type="checkbox"
                checked={settings.useIterativeDeepening}
                onChange={(e) =>
                  handleBooleanChange("useIterativeDeepening", e.target.checked)
                }
              />
              <span>Iterative Deepening</span>
            </label>
            <p className="feature-description">
              Gradually increase search depth for better moves
            </p>
          </div>

          <div className="checkbox-control">
            <label>
              <input
                type="checkbox"
                checked={settings.boardControl}
                onChange={(e) =>
                  handleBooleanChange("boardControl", e.target.checked)
                }
              />
              <span>Board Control Evaluation</span>
            </label>
            <p className="feature-description">
              Assess control of key squares and center
            </p>
          </div>

          <div className="checkbox-control">
            <label>
              <input
                type="checkbox"
                checked={settings.pawnStructure}
                onChange={(e) =>
                  handleBooleanChange("pawnStructure", e.target.checked)
                }
              />
              <span>Pawn Structure Analysis</span>
            </label>
            <p className="feature-description">
              Evaluate pawn formations and weaknesses
            </p>
          </div>
        </div>

        {/* Move Priority Order - TypeScript engine only */}
        {settings.engineType === "typescript" && (
        <div className="settings-section">
          <h2>Move Priority Order</h2>
          <p className="section-description">
            Drag or use arrows to reorder how the AI prioritizes different types
            of moves (TypeScript engine only)
          </p>
          <div className="priority-list">
            {settings.priorityOrder.map((priority, index) => (
              <div key={priority} className="priority-item">
                <span className="priority-rank">{index + 1}</span>
                <span className="priority-label">
                  {PRIORITY_LABELS[priority]}
                </span>
                <div className="priority-controls">
                  <button
                    className="priority-btn"
                    onClick={() => handlePriorityChange(index, "up")}
                    disabled={index === 0}
                  >
                    ▲
                  </button>
                  <button
                    className="priority-btn"
                    onClick={() => handlePriorityChange(index, "down")}
                    disabled={index === settings.priorityOrder.length - 1}
                  >
                    ▼
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Save Config Dialog */}
      {showSaveDialog && (
        <div className="config-dialog-overlay" onClick={closeSaveDialog}>
          <div className="config-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Save AI Configuration</h3>
            {configSuccess ? (
              <>
                <div className="config-dialog-success">{configSuccess}</div>
                <div className="config-dialog-actions">
                  <button
                    className="config-dialog-btn save"
                    onClick={closeSaveDialog}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="config-dialog-field">
                  <label>Configuration Name *</label>
                  <input
                    type="text"
                    value={configName}
                    onChange={(e) => setConfigName(e.target.value)}
                    placeholder="e.g., Aggressive Attacker"
                    maxLength={50}
                  />
                </div>
                <div className="config-dialog-field">
                  <label>Description (optional)</label>
                  <textarea
                    value={configDescription}
                    onChange={(e) => setConfigDescription(e.target.value)}
                    placeholder="Describe this configuration..."
                    maxLength={200}
                    rows={3}
                  />
                </div>
                {configError && (
                  <div className="config-dialog-error">{configError}</div>
                )}
                <div className="config-dialog-actions">
                  <button
                    className="config-dialog-btn cancel"
                    onClick={closeSaveDialog}
                  >
                    Cancel
                  </button>
                  <button
                    className="config-dialog-btn save"
                    onClick={handleSaveConfig}
                    disabled={!configName.trim() || isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Configuration"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notification Modal (replaces native alert/confirm dialogs) */}
      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        confirmText={notification.confirmText}
        cancelText={notification.cancelText}
        onClose={() => {
          setNotification(initialNotificationState);
          setPendingDeleteConfig(null);
        }}
        onConfirm={pendingDeleteConfig ? confirmDeleteConfig : undefined}
      />
    </div>
  );
};

export default AISettings;
